import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAccountStatus } from '../context/AccountStatusContext';

/**
 * Global gate: when the logged-in dealer has new_orders_blocked = true, the
 * only routes they can use are payment-related (invoices, order detail to pay,
 * profile, dashboard, login). Everything else routes them to /invoices and
 * pops a modal that says all outstanding invoices must be paid first.
 *
 * Mounted near the top of the Layout so every page goes through it.
 */
const AUTH_PREFIXES = [
  '/login',
  '/forgot-password',
  '/reset-password',
  '/impersonate',
  '/onboard',
];

/**
 * When a dealer is blocked, the ONLY thing they can do is pay an invoice.
 * Allowed surfaces:
 *   - /invoices and /invoices/:id          (browse + pay invoices)
 *   - /orders/:id (specific order detail)  (the "Pay" button on an invoice
 *     navigates to the order detail to start checkout)
 *   - /orders/:id/confirmation            (Stripe success redirect)
 *   - /orders/:id/payment-cancelled       (Stripe cancel redirect)
 *   - /login + auth pages                  (always)
 *
 * Everything else — / (dashboard), /orders (list), /orders/new, /orders/:id/edit,
 * /profile, /quote, /price-builder, /parts, /designer, /email-templates,
 * /shipments, /leads, /installs, etc. — is blocked.
 */
function isAllowed(pathname: string): boolean {
  if (AUTH_PREFIXES.some(p => pathname === p || pathname.startsWith(p + '/'))) return true;
  if (pathname === '/invoices' || pathname.startsWith('/invoices/')) return true;
  // /orders/:id and /orders/:id/<sub> — but block /orders (list), /orders/new, /orders/:id/edit
  const orderMatch = pathname.match(/^\/orders\/([^\/]+)(\/.*)?$/);
  if (orderMatch) {
    const orderId = orderMatch[1];
    const sub = orderMatch[2] || '';
    if (orderId === 'new') return false;
    if (sub === '/edit') return false;
    return true;
  }
  return false;
}

export function BlockedAccountGate({ children }: { children: React.ReactNode }) {
  const { newOrdersBlocked, newOrdersBlockReason, loading } = useAccountStatus();
  const location = useLocation();
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    if (loading) return;
    if (!newOrdersBlocked) {
      setShowModal(false);
      return;
    }
    if (!isAllowed(location.pathname)) {
      setShowModal(true);
    } else {
      setShowModal(false);
    }
  }, [newOrdersBlocked, loading, location.pathname]);

  const dismissAndRedirect = () => {
    setShowModal(false);
    navigate('/invoices', { replace: true });
  };

  return (
    <>
      {/* Persistent banner whenever dealer is blocked — shows on every page */}
      {!loading && newOrdersBlocked && (
        <div className="bg-red-600 text-white px-4 py-2 text-sm sticky top-0 z-40 flex items-center justify-between gap-3">
          <span>
            <strong>Account on hold:</strong>{' '}
            {newOrdersBlockReason || 'All outstanding invoices must be paid before placing new orders.'}
          </span>
          <button
            onClick={() => navigate('/invoices')}
            className="bg-white text-red-700 font-semibold text-xs px-3 py-1 rounded hover:bg-red-50 whitespace-nowrap"
          >
            View Invoices
          </button>
        </div>
      )}

      {children}

      {/* Modal pops when blocked dealer tries to land on a non-allowed route */}
      {showModal && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-900 rounded-lg shadow-xl max-w-md w-full p-6 border border-red-200 dark:border-red-700">
            <div className="flex items-start gap-3 mb-4">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-100 dark:bg-red-900/40 flex items-center justify-center">
                <svg className="w-5 h-5 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
                  Account on Hold
                </h3>
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  All outstanding invoices must be paid in order to use this function.
                </p>
                {newOrdersBlockReason && (
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-2 italic">
                    {newOrdersBlockReason}
                  </p>
                )}
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <button
                onClick={dismissAndRedirect}
                className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg text-sm font-semibold"
              >
                Go to My Invoices
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
